// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Standalone gating helper for the WidgetBrowser. Kept in its own file so
// the unit test can import it without dragging the WIDGET_REGISTRY (and
// downstream Modal/Recharts imports) through jsdom.

/**
 * True when the housevisits module is enabled on the tenant. Accepts both
 * the normalised boolean shape returned by `useSettings()` and the raw
 * shapes stored on CompagnyMeta (`true`, `'true'`) — defensive so a stale
 * subscription or unusual tenant doc doesn't silently hide every widget.
 *
 * @param {unknown} flag
 */
export const housevisitsOn = (flag) => flag === true || flag === 'true';

/**
 * True when the personas module is enabled on the tenant. Same defensive
 * widening as housevisitsOn — accept both the dashboard's 'flex' sentinel
 * and the raw 'true' / boolean shapes.
 *
 * @param {unknown} flag
 */
export const personasOnFlag = (flag) => flag === 'flex' || flag === true || flag === 'true';

/**
 * Check whether a widget's `getRequiredModule()` is satisfied by the current
 * tenant's module flags. Widgets that return null are always visible.
 *
 * In template mode (`isTemplate === true`) every gated widget is shown
 * regardless of the current tenant's flags — templates are cross-tenant and
 * the template builder must be able to compose widgets for any module.
 *
 * Supported requiredModule values:
 *   - 'housevisits' — requires moduleFlags.housevisits truthy
 *   - 'personas'    — requires moduleFlags.personasOn === 'flex'
 *   - null          — always visible
 *
 * @param {{ prototype?: { getRequiredModule?: () => 'housevisits' | 'personas' | null } }} WidgetClass
 * @param {{ housevisits?: unknown, personasOn?: unknown }} moduleFlags
 * @param {boolean} [isTemplate=false]
 * @returns {boolean}
 */
export const isModuleEnabled = (WidgetClass, moduleFlags, isTemplate = false) => {
    if (isTemplate) return true;
    const required = WidgetClass?.prototype?.getRequiredModule?.() || null;
    if (required === 'housevisits') return housevisitsOn(moduleFlags?.housevisits);
    if (required === 'personas') return personasOnFlag(moduleFlags?.personasOn);
    return true;
};

/**
 * Whether a widget can be picked in a report about one participant.
 * Widgets whose `countsAllPersonas()` is true count over every participant
 * and say nothing about one person, so they are hidden once the report has a
 * participant. Without a participant (`null`) nothing changes.
 *
 * AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T5)
 *
 * @param {{ prototype?: { countsAllPersonas?: () => boolean } }} WidgetClass
 * @param {string|null} personaId - From useWidgetPersona(): a PersonaID or null
 * @returns {boolean}
 */
export const isAvailableForPersona = (WidgetClass, personaId) => {
    if (!personaId) return true;
    return WidgetClass?.prototype?.countsAllPersonas?.() !== true;
};
